import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector, RootState, loadClients } from '../../redux';
import { ChangeListItem, Icon } from "..";
import { useNavigate } from "react-router-dom";
import { Change, Client, Project } from "../../models";
import { Avatar, Badge } from "flowbite-react";
import { t } from "i18next";
import { clientPath } from '../../utils/clientPath';
import SearchResultSkeleton from './SearchResultSkeleton';

interface SearchResultProps {
    query: string;
    hasFilters?: boolean;
    onClose: () => void;
}

const clientLabel = (client: Client): string => {
    if (typeof client.name === 'function')
        return client.name();
    return [client.id, client.company, client.firstname, client.lastname]
        .filter(Boolean)
        .join(' - ');
};

const SearchResult: React.FC<SearchResultProps> = ({query, hasFilters = false, onClose}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const changes  = useAppSelector((state: RootState) => state.search.results.changes);
    const projects = useAppSelector((state: RootState) => state.search.results.projects);
    const loading = useAppSelector((state: RootState) => state.search.loading);
    const activeQuery = useAppSelector((state: RootState) => state.search.query);
    const { clients } = useAppSelector((state: RootState) => state.clients);
    const { user } = useAppSelector((state: RootState) => state.auth);

    const trimmedQuery = String(query || '').trim();
    const showSkeleton = trimmedQuery.length >= 3 && (
        loading === 'pending'
        || (loading !== 'failed' && activeQuery.trim() !== trimmedQuery)
    );

    useEffect(() => {
        if (user?.can('client:read') && !clients?.length)
            dispatch(loadClients());
    }, [dispatch, user, clients?.length]);

    const matchedClients = useMemo(() => {
        if (!user?.can('client:read'))
            return [];
        const needle = String(query || '').trim().toLowerCase();
        if (needle.length < 2)
            return [];
        return ((clients as Client[]) || [])
            .filter((client) => {
                const haystack = [
                    clientLabel(client),
                    client.email,
                    client.company,
                    client.firstname,
                    client.lastname,
                    client.id != null ? String(client.id) : '',
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                return haystack.includes(needle);
            })
            .sort((a, b) => (a.id || 0) - (b.id || 0))
            .slice(0, 20);
    }, [clients, query, user]);

    const countResults = (matchedClients?.length || 0)
        + (projects?.length || 0)
        + (changes?.length || 0);

    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        setSelectedIndex(0);
    }, [query, hasFilters, countResults]);

    useEffect(() => {
        function handleKeyPress(event: any) {
            if (event.key === "ArrowDown") {
                setSelectedIndex((prevIndex) =>
                    prevIndex + 1 >= countResults ? 0 : prevIndex + 1
                );
            } else if (event.key === "ArrowUp") {
                setSelectedIndex((prevIndex) =>
                    prevIndex - 1 < 0 ? countResults - 1 : prevIndex - 1
                );
            } else if (event.key === "Enter") {
                (document.querySelector('.selectedResult') as HTMLElement)?.click();
            }
        }

        window.addEventListener("keydown", handleKeyPress);

        return () => {
            window.removeEventListener("keydown", handleKeyPress);
        };

    }, [countResults, selectedIndex]);

    if (showSkeleton)
        return <SearchResultSkeleton rows={5} />;

    if (countResults === 0 && (trimmedQuery !== "" || hasFilters))
        return (
            <div>
                <h3 className="text-sm font-light text-center mt-3">
                    <Icon type="sad" className="inline mr-1.5" size={6} />
                    {t("search.no_result") || ""}
                </h3>
            </div>
        );

    let index = 0;
    return (
        <div>
            {matchedClients.length > 0 && (
                <div className="mt-3">
                    <h3 className="text-sm font-bold">
                        <Icon type="client" className="inline mr-1.5" />
                        {t('search.clients')}&nbsp;({matchedClients.length})
                    </h3>
                    {matchedClients.map((result) => (
                        <div
                            key={result._id}
                            onClick={() => {
                                navigate(clientPath(result._id));
                                onClose();
                            }}
                            className={`border rounded p-2 cursor-pointer mt-2 flex items-center justify-between ${index++ === selectedIndex ? "bg-gray-100 dark:bg-gray-800 selectedResult" : ""}`}
                        >
                            <div className="flex items-center space-x-2 min-w-0">
                                <Avatar img={result.getAvatarUrl()} className="inline w-fit shrink-0"/>
                                <div className="flex flex-col min-w-0">
                                    <h4 className="text-sm font-light heading-md truncate">
                                        {result.company || clientLabel(result)}
                                    </h4>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {[result.fullname(), result.email, result.id != null ? `#${result.id}` : '']
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </div>
                                </div>
                            </div>
                            <Icon type="client" className="shrink-0 text-gray-400" />
                        </div>
                    ))}
                </div>
            )}

            {projects?.length > 0 && (
                <div className="mt-3">
                    <h3 className="text-sm font-bold">
                        <Icon type="project" className="inline mr-1.5" />
                        {t('search.projects')}&nbsp;({projects.length})
                    </h3>
                    {projects.sort((a: Project, b: Project) => a.name.localeCompare(b.name)).map((result) => (
                        <div
                            key={result._id}
                            onClick={() => {
                                navigate(Project.path(result) + '/');
                                onClose();
                            }}
                            className={`border rounded p-2 cursor-pointer mt-2 flex items-center justify-between ${index++ === selectedIndex ? "bg-gray-100 dark:bg-gray-800 selectedResult" : ""}`}
                        >
                            <div className="flex items-center space-x-2">
                                <Avatar img={result.getAvatarUrl()} className="inline w-fit"/>
                                <div className="flex flex-col">
                                    <div className="flex-1">
                                        <h4 className={`text-sm font-light heading-md`}>{result.name}</h4>
                                    </div>
                                    {result.lastRelease?.version &&
                                        <Badge className="w-fit">{result.lastRelease?.version}</Badge>
                                    }
                                </div>
                            </div>

                            <div className="flex items-center">
                                <div
                                    className="w-[80px] text-center whitespace-nowrap font-medium text-gray-900 dark:text-white"
                                >
                                    <Icon type="commit" className="inline"/>&nbsp;
                                    <strong>{result.nbCommits}</strong>
                                </div>
                                <div
                                    className="w-[80px] text-center whitespace-nowrap font-medium text-gray-900 dark:text-white"
                                >
                                    <Icon type="release" className="inline"/>&nbsp;
                                    <strong>{result.nbReleases}</strong>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {changes?.length > 0 && (
                <div className="mt-3">
                    <h3 className="text-sm font-bold">
                        <Icon type="board" className="inline mr-1.5" />
                        {t('search.changes')}&nbsp;({changes.length})
                    </h3>
                    {changes.sort((a: Change, b: Change) => b.slug - a.slug).map((result) => (
                        <ChangeListItem
                            className={`mt-2 ${(index === selectedIndex) ? "selectedResult" : ""}`}
                            change={result}
                            key={result._id}
                            tabIndex={index++ === selectedIndex ? 0 : -1}
                            showProject
                            showRequester
                            onClick={() => {
                                navigate(Project.path(result.project, 'change', result._id));
                                onClose();
                            }}
                        />
                    ))}
                </div>
            )}

            <div className="mt-4 flex justify-between gap-3 text-xs">
                <div className="flex flex-wrap items-center gap-1">
                    <span>{t("search.open_search_bar") || ""}</span>
                    <kbd>shift</kbd>
                    <kbd>/</kbd>
                    <span className="mx-1 text-gray-400">·</span>
                    <kbd>ctrl</kbd>
                    <kbd>k</kbd>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                    {t("search.close_search_bar") || ""}
                    <kbd>esc</kbd>
                </div>
            </div>
        </div>
    );
};

export default SearchResult;
